import { useState } from 'react';
import { Briefcase, Trash } from 'lucide-react';
import { angularZipError } from '../../../utils/aboutYourselfAngularInlineMessages';
import { EditDatePicker } from '@/components/ui/EditDatePicker';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';
import type { PlaceResult } from '@/hooks/useGooglePlacesAutocomplete';
import { useGoogleTranslateRefresh } from '@/hooks/useGoogleTranslateRefresh';
import { parseAddressComponents } from '@/lib/utils/addressParser';
import { CurrencyInput } from '@/components/ui/CurrencyInput';
import { resolveStateToCode, type State } from '@/lib/api/statesService';
import {
  deriveJobDurationMonths,
  hasTruthyJobGapValue,
  shouldShowJobGapExplanation,
  isJobGapExplanationRequired,
  hrManagerContactEmailError,
} from '../../../utils/incomeEmploymentValidator';
import type { EmploymentFormRow } from '../../../types/tabs';
import styles from '../../../styles/applicationDetail.module.css';
import editStyles from '../../../styles/editForm.module.css';
import { EMPLOYMENT_TYPES, INCOME_AMOUNT_MAX_DIGITS_BEFORE_DECIMAL } from '../../../constants';
import {
  isUnitedStatesCountry,
  incomeJobTestId,
  calculateYearlyIncome,
  buildEmployerAddressForDistance,
  buildGoogleMapsDirectionsUrl,
  parseComparableDate,
  isEndDateBeforeStartDate,
  parseJobDistance,
} from './incomeEmploymentHelpers';

const TEXT_FIELD_BASE =
  'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500';
const SELECT_FIELD_BASE =
  'w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500';

function withInputError(base: string, hasError: boolean): string {
  return hasError ? `${base} ${editStyles.inputError}` : base;
}

export function EmploymentEditForm({
  formData,
  onChange,
  onBatchChange,
  employmentType,
  onAddressBlur,
  onAddressResolved,
  onTranslateBlur,
  propertyAddress = "",
  statesList = [],
  showRemove = false,
  onRemove,
  onMakeOther,
  makeOtherLabel,
  rowIndex = 0,
  submitAttempted = false,
}: {
  formData: EmploymentFormRow;
  onChange: (field: string, value: string | number | boolean) => void;
  /** Single state update when Google Places returns multiple address fields. */
  onBatchChange?: (updates: Partial<EmploymentFormRow>) => void;
  employmentType: string;
  onAddressBlur?: () => void;
  /** Angular translateContent: translate a free-text field to English on blur. */
  onTranslateBlur?: (field: string, value: string) => void;
  onAddressResolved?: (updates: Partial<EmploymentFormRow>) => void;
  propertyAddress?: string;
  statesList?: State[];
  showRemove?: boolean;
  onRemove?: () => void;
  onMakeOther?: () => void;
  makeOtherLabel?: string;
  rowIndex?: number;
  /** When true, the parent has attempted to submit so required-field errors render. */
  submitAttempted?: boolean;
}) {
  const [showAddressDetail, setShowAddressDetail] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  /** Outside-U.S. address: free-text state. U.S. (incl. `US` from APIs) uses State dropdown with full names. */
  const isOutsideUS = !isUnitedStatesCountry(formData.company_country);

  const addressDetailFields = [
    "company_address",
    "company_city",
    "company_state",
    "company_zipcode",
    "company_country",
  ] as const;
  const validationRequiresAddressDetail =
    submitAttempted &&
    (addressDetailFields.some((field) => {
      const value = formData[field];
      return value === undefined || value === null || String(value).trim() === "";
    }) ||
      angularZipError(formData.company_zipcode) !== null);
  const isAddressDetailVisible = showAddressDetail || validationRequiresAddressDetail;

  // React mounts this employer row / its address detail on demand; re-run Google
  // Translate so Spanish applies to the newly added fields (Angular keeps them in-DOM).
  useGoogleTranslateRefresh(isAddressDetailVisible);

  const markTouched = (field: string) =>
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
  const shouldShowError = (field: string) =>
    submitAttempted || !!touched[field];
  const requiredError = (field: string, label: string): string | null => {
    if (!shouldShowError(field)) return null;
    const value = formData[field as keyof EmploymentFormRow];
    const isEmpty =
      value === null || value === undefined || String(value).trim() === "";
    return isEmpty ? `'${label}' is required.` : null;
  };
  const isFutureDate = (value: unknown): boolean => {
    const date = parseComparableDate(value);
    if (!date) return false;
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return date.getTime() > today.getTime();
  };
  const startDateError = (): string | null => {
    const required = requiredError("start_date", "Job Start Date");
    if (required) return required;
    if (
      employmentType === "previous_job" &&
      shouldShowError("start_date") &&
      isFutureDate(formData.start_date)
    ) {
      return "Date should not be in the Future.";
    }
    return null;
  };
  const endDateError = (): string | null => {
    if (employmentType !== "previous_job") return null;
    const required = requiredError("end_date", "Job End Date");
    if (required) return required;
    if (shouldShowError("end_date") && isFutureDate(formData.end_date)) {
      return "Date should not be in the Future.";
    }
    if (
      (shouldShowError("start_date") || shouldShowError("end_date")) &&
      isEndDateBeforeStartDate(formData.start_date, formData.end_date)
    ) {
      return "Job End Date should be after Job Start Date.";
    }
    return null;
  };
  const zipError = (): string | null => {
    if (!shouldShowError("company_zipcode")) return null;
    return angularZipError(formData.company_zipcode);
  };

  /** Angular: `*ngIf="job_gap && job_gap.value"` after `validateEmployer()` patches gap. */
  const showJobGapBlock =
    employmentType === "previous_job" &&
    Boolean(String(formData.end_date ?? "").trim()) &&
    hasTruthyJobGapValue(formData.job_gap);
  const showJobGapExplanation =
    showJobGapBlock && shouldShowJobGapExplanation(formData.job_gap);
  const jobGapExplanationRequired = isJobGapExplanationRequired(formData.job_gap);
  const employerAddressForMap = buildEmployerAddressForDistance(formData);
  const mapUrl =
    employmentType === "current_job" &&
    propertyAddress &&
    employerAddressForMap
      ? buildGoogleMapsDirectionsUrl(propertyAddress, employerAddressForMap)
      : "";
  const jobDistanceNum =
    parseJobDistance(formData.job_distance) ||
    parseJobDistance(formData.distance);
  const showDistanceBlock = employmentType === "current_job" && jobDistanceNum > 50;

  const distanceExplanationError = (): string | null => {
    if (!showDistanceBlock) return null;
    if (!shouldShowError("distance_explain")) return null;
    const value = String(formData.distance_explain ?? "").trim();
    return value
      ? null
      : "Situation/Explanation is required when property is more than 50 miles from employer.";
  };

  const jobGapExplanationError = (): string | null => {
    if (!showJobGapExplanation || !jobGapExplanationRequired) return null;
    if (!shouldShowError("job_gap_explanation")) return null;
    const value = String(formData.job_gap_explanation ?? "").trim();
    if (!value) return "'Job Gap Explanation' is required.";
    if (value.length > 200) {
      return "'Job Gap Explanation' must be 200 characters or less.";
    }
    return null;
  };

  const tid = (slug: string, suffix: string) =>
    incomeJobTestId(employmentType, rowIndex, slug, suffix);
  const employmentTypeRadioGroupName = `employment_type_${employmentType}_${rowIndex}`;

  const companyNameErr = requiredError("company_name", "Company Name");
  const companyAddressErr = requiredError("company_address", "Employer Street");
  const companyCityErr = requiredError("company_city", "Employer City");
  const companyStateErr = requiredError("company_state", "Employer State");
  const companyCountryErr = requiredError("company_country", "Country");
  const zipErr = zipError();
  const monthlyIncomeErr = requiredError("monthly_income", "Monthly Income");
  const startDateErrMsg = startDateError();
  const endDateErrMsg = endDateError();
  const distanceExplainErr = distanceExplanationError();
  const jobGapExplainErr = jobGapExplanationError();
  const hrEmailErr =
    employmentType === "current_job" &&
    shouldShowError("hr_manager_contact_email")
      ? hrManagerContactEmailError(formData.hr_manager_contact_email)
      : null;

  const employmentTypeControls = (
    <div className={styles.incomeEmploymentTypeHeaderGroup}>
      <label
        className={styles.incomeEmploymentTypeHeaderLabel}
        data-testid={tid("employment-type", "label")}
      >
        Type of Employment<span className="text-red-500">*</span>
      </label>
      <div className={styles.incomeEmploymentTypeHeaderOptions}>
        {Object.entries(EMPLOYMENT_TYPES).map(([key, value]) => (
          <label
            key={key}
            className={styles.incomeEmploymentTypeHeaderOption}
            data-testid={tid(`employment-type-${key.toLowerCase().replace(/_/g, "-")}`, "label")}
          >
            <input
              type="radio"
              name={employmentTypeRadioGroupName}
              value={key}
              checked={formData.employment_type === key}
              data-testid={tid(`employment-type-${key.toLowerCase().replace(/_/g, "-")}`, "input")}
              onChange={(e) => onChange("employment_type", e.target.value)}
              className="w-4 h-4 text-blue-500 border-gray-300 focus:ring-blue-500"
            />
            <span>{value}</span>
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <div className={styles.incomeEmploymentJobCard}>
      <div className={styles.incomeEmploymentJobCardHeader}>
        {employmentTypeControls}
        {(showRemove || onMakeOther) && (
          <div className={styles.incomeEmploymentJobActions}>
            {onMakeOther && makeOtherLabel && (
              <button
                type="button"
                className={styles.incomeMakeJobButton}
                data-testid={tid("make-other", "button")}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onMakeOther();
                }}
              >
                <Briefcase className="h-3.5 w-3.5" aria-hidden />
                {makeOtherLabel}
              </button>
            )}
            {showRemove && onRemove && (
              <button
                type="button"
                className={styles.incomeRemoveButton}
                data-testid={tid("remove", "button")}
                onClick={onRemove}
              >
                <Trash className="h-3.5 w-3.5" aria-hidden />
                Remove
              </button>
            )}
          </div>
        )}
      </div>

      <div className={`${styles.incomeEmploymentJobFieldsGrid} grid grid-cols-12 gap-6`}>
        {/* Row 1: Company Name | Address (employer_street_address) | View Detail | Outside U.S.
            Matches Angular `application-add` employer row layout. */}
        <div className="col-span-12 md:col-span-3">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={tid("company-name", "label")}>
            Company Name<span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className={withInputError(TEXT_FIELD_BASE, Boolean(companyNameErr))}
            value={formData.company_name}
            data-testid={tid("company-name", "input")}
                onChange={(e) => onChange("company_name", e.target.value)}
            onBlur={() => markTouched("company_name")}
            placeholder="Company Name"
            required
          />
          {companyNameErr && (
            <p className="text-red-500 text-xs mt-1">
              {companyNameErr}
            </p>
          )}
        </div>

        {/* Angular `employer_street_address` — combined address line wired to Google Places.
            `getPlaceAutocomplete('current_employment'/'previous_employment')` populates each
            detail field (street/city/state/zipcode/country) — same behavior here. */}
        <div className="col-span-12 md:col-span-4">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={tid("employer-address", "label")}>
            Address
          </label>
          <div data-testid={tid("employer-address", "input")}>
            <AddressAutocomplete
              value={formData.employer_street_address || ""}
              onChange={(value) => onChange("employer_street_address", value)}
              onPlaceSelect={(place: PlaceResult) => {
                const parsed = parseAddressComponents(place);
                const formattedAddress = parsed.formattedAddress || "";
                const streetLine =
                  [parsed.streetNumber, parsed.streetName]
                    .filter(Boolean)
                    .join(" ")
                    .trim() || parsed.streetName || "";
                const countryCode = parsed.country || "";
                const country = countryCode
                  ? isUnitedStatesCountry(countryCode)
                    ? "USA"
                    : countryCode
                  : formData.company_country || "USA";

                const updates: Partial<EmploymentFormRow> = {
                  employer_street_address: formattedAddress,
                  company_address: streetLine,
                  company_city: parsed.city || "",
                  company_state: parsed.state || "",
                  company_zipcode: parsed.zipCode || "",
                  company_country: country,
                };

                if (onBatchChange) {
                  onBatchChange(updates);
                } else {
                  Object.entries(updates).forEach(([field, value]) =>
                    onChange(field, value),
                  );
                }
                setShowAddressDetail(true);
                if (employmentType === "current_job") {
                  onAddressResolved?.(updates);
                }
              }}
              placeholder="Enter your address"
              label=""
            />
          </div>
        </div>

        <div className="col-span-6 md:col-span-2 flex items-end gap-2">
          <button
            type="button"
            className="flex-1 py-2 bg-[#007bff] hover:bg-[#0069d9] text-white rounded-md text-sm font-medium transition-colors"
            data-testid={tid("view-address-detail", "button")}
                onClick={() => setShowAddressDetail((v) => !v)}
          >
            {isAddressDetailVisible ? "Close Detail" : "View Detail"}
          </button>
          {mapUrl ? (
            <a
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-md bg-[#007bff] px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-[#0069d9]"
              data-testid={tid("map", "link")}
            >
              Map
            </a>
          ) : null}
        </div>

        {/* Outside U.S. checkbox lives on the top row per Angular */}
        <div className="col-span-6 md:col-span-3 flex items-end">
          <label className="flex items-center gap-2 cursor-pointer pb-2" data-testid={tid("outside-us", "label")}>
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-gray-300 text-blue-500"
              checked={!isUnitedStatesCountry(formData.company_country)}
              data-testid={tid("outside-us", "checkbox")}
                onChange={(e) => {
                onChange(
                  "company_country",
                  e.target.checked ? "" : "USA",
                );
              }}
            />
            <span className="text-sm text-gray-700">
              Other / Outside U.S.
            </span>
          </label>
        </div>

        {/* Address Detail (Angular "View Detail" toggle) */}
        {isAddressDetailVisible && (
          <>

            <div className="col-span-12 md:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={tid("street", "label")}>
                Street<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={withInputError(TEXT_FIELD_BASE, Boolean(companyAddressErr))}
                value={formData.company_address || ""}
                data-testid={tid("street", "input")}
                onChange={(e) => onChange("company_address", e.target.value)}
                onBlur={() => {
                  markTouched("company_address");
                  onAddressBlur?.();
                }}
                placeholder="Street"
                required
              />
              {companyAddressErr && (
                <p className="text-red-500 text-xs mt-1">
                  {companyAddressErr}
                </p>
              )}
            </div>
            <div className="col-span-12 md:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={tid("city", "label")}>
                City<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={withInputError(TEXT_FIELD_BASE, Boolean(companyCityErr))}
                value={formData.company_city || ""}
                data-testid={tid("city", "input")}
                onChange={(e) => onChange("company_city", e.target.value)}
                onBlur={() => {
                  markTouched("company_city");
                  onAddressBlur?.();
                }}
                placeholder="City"
                required
              />
              {companyCityErr && (
                <p className="text-red-500 text-xs mt-1">
                  {companyCityErr}
                </p>
              )}
            </div>
            <div className="col-span-12 md:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={tid("state", "label")}>
                State<span className="text-red-500">*</span>
              </label>
              {isOutsideUS ? (
                <input
                  type="text"
                  className={withInputError(TEXT_FIELD_BASE, Boolean(companyStateErr))}
                  value={formData.company_state || ""}
                  data-testid={tid("state", "input")}
                onChange={(e) => onChange("company_state", e.target.value)}
                  onBlur={() => {
                    markTouched("company_state");
                    onAddressBlur?.();
                  }}
                  placeholder="State"
                  required
                />
              ) : (
                <select
                  className={withInputError(SELECT_FIELD_BASE, Boolean(companyStateErr))}
                  value={resolveStateToCode(
                    formData.company_state || "",
                    statesList,
                  )}
                  data-testid={tid("state", "select")}
                onChange={(e) => onChange("company_state", e.target.value)}
                  onBlur={() => {
                    markTouched("company_state");
                    onAddressBlur?.();
                  }}
                  required
                  disabled={statesList.length === 0}
                  aria-busy={statesList.length === 0}
                >
                  <option value="">
                    {statesList.length === 0
                      ? "Loading states…"
                      : "Select State"}
                  </option>
                  {statesList.map((s) => (
                    <option key={s.state_code} value={s.state_code}>
                      {s.state_name}
                    </option>
                  ))}
                </select>
              )}
              {companyStateErr && (
                <p className="text-red-500 text-xs mt-1">
                  {companyStateErr}
                </p>
              )}
            </div>
            <div className="col-span-12 md:col-span-6">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={tid("zipcode", "label")}>
                Zipcode<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={withInputError(TEXT_FIELD_BASE, Boolean(zipErr))}
                value={formData.company_zipcode || ""}
                data-testid={tid("zipcode", "input")}
                onChange={(e) => onChange("company_zipcode", e.target.value)}
                onBlur={() => {
                  markTouched("company_zipcode");
                  onAddressBlur?.();
                }}
                maxLength={6}
                placeholder="Zipcode"
                required
              />
              {zipErr && (
                <p className="text-red-500 text-xs mt-1">{zipErr}</p>
              )}
            </div>
            <div className="col-span-12 md:col-span-6">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={tid("country", "label")}>
                Country<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={withInputError(TEXT_FIELD_BASE, Boolean(companyCountryErr))}
                value={formData.company_country || ""}
                data-testid={tid("country", "input")}
                onChange={(e) => onChange("company_country", e.target.value)}
                onBlur={() => markTouched("company_country")}
                placeholder="Country"
                required
              />
              {companyCountryErr && (
                <p className="text-red-500 text-xs mt-1">
                  {companyCountryErr}
                </p>
              )}
            </div>
          </>
        )}

        {/* Row 2: Income (label adapts to income_period), Yearly Income.
            Angular `appAmount` directive auto-prefixes "$" and caps before-decimal digits. */}
        <div className="col-span-12 md:col-span-4">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={tid("monthly-income", "label")}>
            {formData.income_period && formData.income_period !== "Per_Month"
              ? `${formData.income_period} Income`
              : "Monthly Income"}
            <span className="text-red-500">*</span>
          </label>
          <CurrencyInput
            data-testid={tid("monthly-income", "input")}
            value={formData.monthly_income != null ? String(formData.monthly_income) : ""}
            onChange={(value) => onChange("monthly_income", value)}
            onBlur={() => markTouched("monthly_income")}
            placeholder="$0.00"
            maxDigitsBeforeDecimal={INCOME_AMOUNT_MAX_DIGITS_BEFORE_DECIMAL}
            className={monthlyIncomeErr ? editStyles.inputError : undefined}
          />
          {monthlyIncomeErr && (
            <p className="text-red-500 text-xs mt-1">
              {monthlyIncomeErr}
            </p>
          )}
        </div>

        <div className="col-span-12 md:col-span-4">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={tid("yearly-income", "label")}>
            Yearly Calculated Income
          </label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-gray-600 cursor-not-allowed"
            value={calculateYearlyIncome(
              formData.monthly_income,
              formData.income_period || "Per_Month",
            )}
            readOnly
            data-testid={tid("yearly-calculated-income", "input")}
          />
        </div>

        {/* Row 3: Job Start Date, End Date (if prev), Duration, Email (if current) */}
        <div className="col-span-12 md:col-span-3">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={tid("job-start-date", "label")}>
            Job Start Date<span className="text-red-500">*</span>
          </label>
          <div className="w-full">
            <EditDatePicker
              value={formData.start_date}
              onChange={(value) => {
                onChange("start_date", value);
                markTouched("start_date");
              }}
              onBlur={() => markTouched("start_date")}
              allowPastDates={true}
              allowFutureDates={employmentType === "current_job"}
              displayFormat="mdy"
              calendarPortalId="liberty-income-employment-datepicker"
              className={startDateErrMsg ? editStyles.inputError : ""}
            />
          </div>
          {startDateErrMsg && (
            <p className="text-red-500 text-xs mt-1">
              {startDateErrMsg}
            </p>
          )}
        </div>

        {employmentType === "previous_job" && (
          <div className="col-span-12 md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={tid("job-end-date", "label")}>
              Job End Date<span className="text-red-500">*</span>
            </label>
            <div className="w-full">
              <EditDatePicker
                value={formData.end_date}
                onChange={(value) => {
                  onChange("end_date", value);
                  markTouched("end_date");
                }}
                onBlur={() => markTouched("end_date")}
                allowPastDates={true}
                allowFutureDates={false}
                displayFormat="mdy"
                calendarPortalId="liberty-income-employment-datepicker"
                className={endDateErrMsg ? editStyles.inputError : ""}
              />
            </div>
            {endDateErrMsg && (
              <p className="text-red-500 text-xs mt-1">
                {endDateErrMsg}
              </p>
            )}
          </div>
        )}

        <div className="col-span-12 md:col-span-3">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={tid("job-duration", "label")}>
            Number of months at this job<span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-gray-600 cursor-not-allowed"
            value={(() => {
              const months = deriveJobDurationMonths(
                formData.start_date,
                formData.end_date,
                { treatAsPresentJob: employmentType === "current_job" },
              );
              return months ? `${months} Months` : "";
            })()}
            readOnly
            placeholder="Job Duration"
            data-testid={tid("job-duration", "input")}
          />
        </div>

        {employmentType === "current_job" && (
          <div className="col-span-12 md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={tid("hr-email", "label")}>
              HR/Manager Contact Email
            </label>
            <input
              type="email"
              className={withInputError(TEXT_FIELD_BASE, Boolean(hrEmailErr))}
              value={formData.hr_manager_contact_email || ""}
              data-testid={tid("hr-email", "input")}
                onChange={(e) =>
                onChange("hr_manager_contact_email", e.target.value)
              }
              onBlur={() => markTouched("hr_manager_contact_email")}
              placeholder="Please enter a company-issued email address."
            />
            {hrEmailErr && (
              <p className="text-red-500 text-xs mt-1">{hrEmailErr}</p>
            )}
          </div>
        )}

        {/* Row 4: Self-Employed Website (Angular: *ngIf="job_type === 'SELF_EMPLOYED'") */}
        {formData.employment_type === "SELF_EMPLOYED" && (
          <div className="col-span-12 md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={tid("website", "label")}>
              Website
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={formData.employer_website || ""}
              data-testid={tid("website", "input")}
                onChange={(e) => onChange("employer_website", e.target.value)}
              placeholder="https://example.com"
            />
          </div>
        )}
      </div>

      {/* Distance warning + explanation - Current Job only. Matches Angular and applicant flow: job_distance > 50. */}
      {showDistanceBlock && (
        <div className={styles.incomeDistanceWarningBlock}>
          <div className={`${styles.incomeEditNote} ${styles.incomeDistanceWarningRibbon}`} role="alert">
            <p className="mb-0">Warning :- Property to move to is more than 50 miles from employer.</p>
          </div>
          <div className={styles.incomeDistanceField}>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={tid("distance-explain", "input")} data-testid={tid("distance-explain", "label")}>
              Describe your employment/commuting situation, e.g., remote worker, satellite office, etc.
              <span className="text-red-500"> *</span>
            </label>
            <textarea
              id={tid("distance-explain", "input")}
              className={`${styles.incomeDistanceTextarea}${distanceExplainErr ? ` ${editStyles.inputError}` : ""}`}
              value={formData.distance_explain ?? ""}
              data-testid={tid("distance-explain", "input")}
              onChange={(e) => onChange("distance_explain", e.target.value)}
              onBlur={(e) => {
                markTouched("distance_explain");
                onTranslateBlur?.("distance_explain", e.target.value);
              }}
              placeholder="Please Explain"
              rows={2}
              maxLength={200}
            />
            {distanceExplainErr && (
              <p className="text-red-500 text-xs mt-1">
                {distanceExplainErr}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Job Gap (Previous Employer only) — Angular: *ngIf job_gap && job_gap.value;
          explanation row hidden when job_gap.value == 0; required when value >= 1. */}
      {showJobGapBlock && (
          <div className="mt-4 grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-4">
              <label
                className="block text-sm font-medium text-gray-700 mb-1"
                htmlFor={tid("job-gap", "input")}
                data-testid={tid("job-gap", "label")}
              >
                Gap between this and {rowIndex === 0 ? "current Job" : "previous job"}
              </label>
              <input
                id={tid("job-gap", "input")}
                type="number"
                className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-gray-600 cursor-not-allowed"
                value={formData.job_gap}
                readOnly
                placeholder="Job Gap"
                data-testid={tid("job-gap", "input")}
              />
            </div>
            {showJobGapExplanation && (
            <div className="col-span-12 md:col-span-6">
              <label
                className="block text-sm font-medium text-gray-700 mb-1"
                htmlFor={tid("job-gap-explanation", "input")}
                data-testid={tid("job-gap-explanation", "label")}
              >
                Job Gap Explanation
                {jobGapExplanationRequired ? (
                  <span className="text-red-500">*</span>
                ) : null}
              </label>
              <textarea
                id={tid("job-gap-explanation", "input")}
                className={withInputError(
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[80px]",
                  Boolean(jobGapExplainErr),
                )}
                value={formData.job_gap_explanation ?? ""}
                data-testid={tid("job-gap-explanation", "input")}
                onChange={(e) =>
                  onChange("job_gap_explanation", e.target.value)
                }
                onBlur={(e) => {
                  markTouched("job_gap_explanation");
                  onTranslateBlur?.("job_gap_explanation", e.target.value);
                }}
                placeholder="Job Gap Description"
                rows={3}
                maxLength={200}
                required={jobGapExplanationRequired}
              />
              {jobGapExplainErr && (
                <p className="text-red-500 text-xs mt-1">
                  {jobGapExplainErr}
                </p>
              )}
            </div>
            )}
          </div>
        )}
    </div>
  );
}
